import { useEffect } from 'react';
import { analysisWsClient } from 'ws/analysisWsClient';
import { useAuthStore } from 'store/useAuthStore';

/**
 * Drives the singleton analysis WebSocket off auth state. Mount ONCE near the
 * root of the tree (App.tsx). The client is idempotent, so a StrictMode
 * double-mount is harmless.
 *
 * The backend gates /ws/analysis on a valid JWT, so the socket must only connect
 * once the user is authenticated — and must reconnect (not merely stay up) when
 * auth becomes available, since it connects with whatever token exists at that
 * moment. Reacting to `status` covers every transition in one place: boot
 * ('unknown' → no connect), a stored session resolved by bootstrap(), fresh
 * login, and logout (drops the now-revoked JWT). start() is left implicit —
 * reconnect() both (re)starts and resets the backoff attempt counter.
 */
export function useAnalysisWebSocket(): void {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status === 'authenticated') {
      analysisWsClient.start();
      analysisWsClient.reconnect();
    } else {
      // 'unknown' (bootstrapping) or 'anonymous' (logged out) — no socket.
      analysisWsClient.stop();
    }
    return () => {
      // App unmount / HMR — tear down cleanly.
      analysisWsClient.stop();
    };
  }, [status]);
}
